import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useContext } from 'react';
import { TenantConfig, TenantConfigContext } from 'context/TenantConfigContext';
import { useModelService, Measure } from 'services/model/model.service';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { DrillDownTableDateGmvValues } from 'pages/trading/outlier/DrillDownTableDateRangeRow';
import { buildDrillDownTableCoreColumns } from 'pages/trading/outlier/DrilldownCoreColumns';
import { MessageSentiment } from 'pages/trading/model';
import { DataTableContextProvider } from 'components/table/context/DataTableContextProvider';
import { DataTable } from 'components/table/DataTable';
import { useCoreDrilldownExportConfigOptions } from 'pages/trading/outlier/DrilldownExportUtil';
import { createArticleImageCell } from 'components/table/DataTableUtil';

export interface ArticleRow extends DrillDownTableDateGmvValues {
  artImageUrl: string;
  prdName: string;
  artEan: string;
  artSize: string;
  artBrandColor: string;
}

const columnHelper = createColumnHelper<ArticleRow>();
const buildColumns = (tenantContext: TenantConfig, headers: string[], intl: IntlShape, measure: Measure) => [
  columnHelper.accessor('artImageUrl', {
    enableSorting: false,
    header: () => <FormattedMessage id="general.label.image" />,
    cell: info => createArticleImageCell(info, tenantContext),
  }),
  columnHelper.accessor('artEan', {
    id: 'artEan',
    header: () => headers[2],
    cell: info => info.getValue(),
  }),
  columnHelper.accessor('artSize', {
    id: 'artSize',
    header: () => headers[1],
    cell: info => info.getValue(),
  }),
  columnHelper.accessor('artBrandColor', {
    id: 'artBrandColor',
    header: () => headers[0],
    cell: info => info.getValue(),
  }),
  ...buildDrillDownTableCoreColumns(intl, measure),
];

export interface ProductsTableProps {
  rows: ArticleRow[];
  measure: Measure;
  sentiment?: MessageSentiment;
  detectionDate?: string;
}

export function ArticlesTable({ rows, measure, sentiment = 'NEUTRAL', detectionDate = undefined }: ProductsTableProps) {
  const { getAttributes } = useModelService();
  const [artBrandColor, artSize, artEan] = getAttributes(['artBrandColor', 'artSize', 'artEan']);

  const tenantContext = useContext(TenantConfigContext);
  const intl = useIntl();

  const columns = buildColumns(
    tenantContext,
    [artBrandColor.name, artSize.name, artEan.name],
    intl,
    measure
  ) as ColumnDef<unknown>[];

  const exportConfigOptions = useCoreDrilldownExportConfigOptions('articles');

  return (
    <DataTableContextProvider>
      <DataTable
        testId="trading-drilldown-articles-table"
        titleId="insights.articles"
        subTitleDecorator={header =>
          `${header}${detectionDate ? ` ${intl.formatMessage({ id: 'trading.drilldown.detection.date' })} ${detectionDate}` : ''}`
        }
        rows={rows}
        columns={columns}
        exportConfigOptions={exportConfigOptions}
        defaultSortingState={[{ id: 'onDayComparisonGmv', desc: sentiment !== 'NEGATIVE' }]}
      />
    </DataTableContextProvider>
  );
}
